import React, { useEffect, useRef, useState } from 'react';
import Surge from './surgeOrder';
import './styles.scss';
import { useSpeechSynthesis } from 'react-speech-kit';
import CardLayout from '../card-layout';
import CardBody from '../card-body';
import CardFooter from '../card-footer';
import { ROUTE_KEYS, STORAGE_KEYS } from '../../helpers/constants';
import { ENUM_ITEM_TYPES } from '../../helpers/enums';
import CardHeader from '../card-header';
import { LiveStatusConfig } from '../../helpers/role-config/live-orders';
import OrderReceipt from '../order-receipt';
import { getCustomButtonConfig } from './custom-button-config';
import UserInformationModal from '../modals/userInformationModal';
import { filterMealsByItemType } from '../../helpers/functions';
import BoxNumberSection from './box-number-section';
import useLayoutContext from '../../contexts/layout.context';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router';
import { statusIsDispatch, statusIsStarted } from '../../helpers/permissions.service';

const OrderCard = function OrderCard({
  order,
  actionDisabled,
  onActionClick,
  actionClick,
  reason,
  riderAssigned,
  alert,
  defaultStatus,
  currentFilter,
  checkOrderValidityForRole,
  hideCardButton,
  isPreOrderPage
}) {
  const { pathname } = useLocation();
  const calculatedOrder = order?.calculated_order;
  const paid = order?.paid;
  const time = order?.created_at || order?.calculated_order?.created_at;
  const pOrderDetails = order?.order_details;
  const orderDetails = pOrderDetails === 'string' ? JSON.parse(pOrderDetails) : pOrderDetails;

  const content = useRef(null);
  const {
    setOrders: setOrdersInContext,
    orders: ordersInContext,
    permissions,
    setReceiptToPrint
  } = useLayoutContext();
  const {
    hasAllItemTypes,
    isShopRole,
    isFrontdeskRole,
    isKitchenRole,
    isPackagingRole,
    isLiquorAdminRole,
    canManageSurge
  } = permissions;
  const [actionLoading, setActionLoading] = useState(false);
  const { speak } = useSpeechSynthesis();
  const [setActive, setActiveState] = useState('activeAccordion');
  const [setHeight, setHeightState] = useState('');
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [setRotate, setRotateState] = useState('accordion__icon rotate');

  const [hasPlayedSound, setHasPlayedSound] = useState(false);
  const [show, setShow] = useState(true);
  const [boxNumber, setBoxNumber] = useState('');

  const [viewDetails, setViewDetails] = useState(true);

  const [status, setStatus] = useState('');
  const [selectedMeals, setSelectedMeals] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [cutleryChecked, setCutleryChecked] = useState(false);
  const [packagingComplete, setPackagingComplete] = useState(false);
  const [finishOrder, setFinishOrder] = useState(false);
  const [subOrderCount, setSubOrderCount] = useState(0);
  const [itemType, setItemType] = useState(ENUM_ITEM_TYPES.FOOD);
  const [disableReceipt, setDisableReceipt] = useState(false);

  const [printStickerMeals, setPrintStickerMeals] = useState([]);

  const role = localStorage.getItem(STORAGE_KEYS.ROLE);
  useEffect(() => {
    let itemType = '';
    if (itemType === 'FOOD') {
      itemType = ENUM_ITEM_TYPES.FOOD;
    } else if (isShopRole) {
      itemType = ENUM_ITEM_TYPES.SHOP;
    } else {
      itemType =
        currentFilter?.key === ENUM_ITEM_TYPES.SHOP ? ENUM_ITEM_TYPES.SHOP : ENUM_ITEM_TYPES.FOOD;
    }
    setItemType(itemType);
  }, [currentFilter]);

  const filterItemType = role === isShopRole ? ENUM_ITEM_TYPES.SHOP : ENUM_ITEM_TYPES.FOOD;

  const orders = hasAllItemTypes
    ? order?.calculated_order?.meals
    : filterMealsByItemType(order?.calculated_order?.meals ?? [], filterItemType);

  const isGroupOrder = !!calculatedOrder?.is_group_order;

  // if order is a group order
  const subOrders = calculatedOrder?.sub_calculated_order_ids?.length
    ? calculatedOrder?.sub_calculated_order_ids.reduce((acc, { meals, ...subOrder }) => {
        const filtered = filterMealsByItemType(meals, filterItemType);
        if (filtered.length > 0) acc.push({ meals: filtered, ...subOrder });
        return acc;
      }, [])
    : [];

  let numberOfBrandAndMeal = null;
  if (isKitchenRole && calculatedOrder.originalMeals) {
    numberOfBrandAndMeal = calculatedOrder.originalMeals.filter((item) => {
      return item.brand.item_type === ENUM_ITEM_TYPES.FOOD;
    });
  }

  useEffect(() => {
    // add other role functions in service file
    const fn = LiveStatusConfig[role];
    const status = defaultStatus ?? fn(order, itemType);
    let subCountTotal = 0;
    order?.calculated_order?.sub_calculated_order_ids?.forEach((item) => {
      const totalSubCount = item?.meals?.reduce((acc, item) => {
        const mealCount = item.meals?.filter((meal) => meal.item_type === itemType)?.length ?? 0;
        return acc + mealCount;
      }, 0);
      subCountTotal += totalSubCount;
    });
    setSubOrderCount(subCountTotal);
    setStatus(status);
  }, [itemType, order]);

  const customActionFn = async () => {
    setActionLoading(true);
    try {
      onActionClick?.();
      if (isShopRole && statusIsStarted(status) && !packagingComplete) {
        return;
      }
      const configFn = getCustomButtonConfig();
      if (configFn) {
        const fn = configFn({ ...order, status }, itemType);
        const isPickupComplete =
          isFrontdeskRole &&
          order.calculated_order.pickup &&
          order.paid &&
          statusIsDispatch(status);
        const payload = isPickupComplete ? order.id : order.order_code;
        const res = await fn?.(payload);
        setBoxNumber(res?.order?.box_number);
        const updatedOrder = { ...order, ...(res?.order || res?.patched_order) };
        const isLiquorLiveOrdersPage = pathname === ROUTE_KEYS.LIQUOR_ADMIN.HOME;
        const isLiquorOrderFinished = updatedOrder?.kitchen_prepared;
        if (checkOrderValidityForRole && !checkOrderValidityForRole(updatedOrder)) {
          if (!isLiquorAdminRole || (isLiquorLiveOrdersPage && isLiquorOrderFinished)) {
            setShow(false);
          }
        }
        setOrdersInContext(
          ordersInContext.map((orderState) =>
            order?.id === orderState?.id ? { ...updatedOrder } : orderState
          )
        );
      } else {
        actionClick(order.order_code);
      }
    } catch (err) {}
    setActionLoading(false);
  };

  useEffect(() => {
    if (boxNumber && isPackagingRole) {
      showBoxAllocationAlert();
      setDisableReceipt(false);
      setReceiptToPrint({ shouldPrint: true, order: getReceiptData() });
    }
  }, [boxNumber]);

  const orderItems = orders?.filter((item) => item.brand.item_type === itemType);
  const mainMealCount = orderItems?.reduce(function (acc, curr) {
    return acc + curr?.meals?.length;
  }, 0);
  const totalMealCount = mainMealCount + subOrderCount;

  const isCutlery = orderDetails?.cutlery ?? true;

  useEffect(() => {
    if (finishOrder) return setPackagingComplete(true);
    const orderCompleted = selectedMeals?.length === totalMealCount;
    const isCutleryPacked = isCutlery ? cutleryChecked : true;
    const isPackagingCompleted = orderCompleted && isCutleryPacked;
    setPackagingComplete(isPackagingCompleted);
  }, [selectedMeals, cutleryChecked, finishOrder]);

  const handleSpeak = () => {
    speak({ text: order.order_code.split('').join(' ') + ' is getting late!' });
    setHasPlayedSound(true);
  };

  useEffect(() => {
    if (order?.kitchen_time_elapsed && !hasPlayedSound) {
      handleSpeak();
    }
  }, [order]);

  useEffect(() => {
    if (isPackagingRole) {
      setDisableReceipt(true);
    }
  }, []);

  const receiptRef = useRef(null);

  const getReceiptData = () => {
    return {
      status,
      calculatedOrder,
      isGroupOrder,
      orders: numberOfBrandAndMeal || orders,
      time,
      order,
      subOrders,
      isCutlery,
      discountValue,
      calculatedGroupTotal: calculatedGroupTotal(),
      subTotal,
      deliveryFee,
      serviceTotal,
      extraCharges,
      calculatedTotal,
      paid,
      amountDue,
      amountDueGroup,
      orderType: order?.order_type,
      boxNumber
    };
  };

  const toggleAccordion = () => {
    setActiveState(setActive === '' ? 'activeAccordion' : '');
    setHeightState(setActive === 'activeAccordion' ? '0px' : `${content.current.scrollHeight}px`);
    setRotateState(setActive === 'activeAccordion' ? 'accordion__icon' : 'accordion__icon rotate');
  };

  const handleMealSelect = async (item) => {
    let newArr = [...selectedMeals, item];
    if (selectedMeals.includes(item)) {
      newArr = newArr.filter((itm) => itm !== item);
    }
    setSelectedMeals(newArr);
  };

  const handleAddonSelect = async (item) => {
    let newArr = [...selectedAddons, item];
    if (selectedAddons.includes(item)) {
      newArr = newArr.filter((itm) => itm !== item);
    }
    setSelectedAddons(newArr);
  };

  const handleBulkAddonSelect = async (items, checkAll) => {
    let newArr = [...selectedAddons];
    if (checkAll) {
      newArr = [...new Set([...newArr, ...items])];
    } else {
      newArr = newArr.filter((addon) => !items.includes(addon));
    }
    setSelectedAddons(newArr);
  };

  // calculated total for single order
  const calculatedTotal = parseInt(calculatedOrder?.total_amount);

  const serviceTotal = parseInt(calculatedOrder?.service_charge);
  const deliveryTotal =
    parseInt(calculatedOrder?.delivery_service_charge) + parseInt(calculatedOrder?.delivery_fee);
  const deliveryFee = calculatedOrder?.free_delivery ? 0 : deliveryTotal;
  const prevAmount = parseInt(calculatedOrder?.prev_price);
  const extraCharges = serviceTotal + deliveryFee;

  const subTotal = calculatedTotal - extraCharges;
  const discountValue =
    prevAmount && !calculatedOrder?.is_group_order ? prevAmount - calculatedTotal : 0;

  const walletAmount = orderDetails?.wallet_amount ? orderDetails?.wallet_amount : 0;

  const amountDue =
    !paid && walletAmount && orderDetails?.use_wallet
      ? calculatedTotal - parseInt(walletAmount)
      : !paid && !orderDetails?.use_wallet
      ? calculatedTotal - 0
      : 0;
  // calculated total for group order
  const calculatedGroupTotal = () => {
    return subTotal;
  };
  const amountDueGroup =
    !paid && walletAmount && orderDetails?.use_wallet
      ? calculatedGroupTotal() - +walletAmount
      : !paid && !orderDetails?.use_wallet
      ? calculatedGroupTotal() - 0
      : 0;
  // get Staff NUmber
  if (order?.kitchen_time_elapsed && !hasPlayedSound && order?.order_code) {
    handleSpeak();
  }

  if (!show) {
    return <div></div>;
  }
  const showBoxAllocationAlert = () => {
    toast.success(`Order ${order.order_code.toUpperCase()} belongs to BOX ${boxNumber} `, {
      autoClose: 10000
    });
  };

  return (
    <CardLayout status={status} order={order}>
      {canManageSurge && <Surge order={order} />}
      <CardHeader
        order={order}
        calculatedOrder={calculatedOrder}
        paid={paid}
        walletAmount={walletAmount}
        isGroupOrder={isGroupOrder}
        actionClick={customActionFn}
        actionLoading={actionLoading}
        packagingComplete={packagingComplete}
        actionDisabled={actionDisabled}
        status={status}
        receiptRef={receiptRef}
        setShowInfoModal={setShowInfoModal}
        hideCardButton={hideCardButton}
        disableReceipt={disableReceipt}
      />
      {statusIsDispatch(status) && (
        <BoxNumberSection
          status={status}
          order={order}
          calculated_order={calculatedOrder}
          time={time}
          setViewDetails={setViewDetails}
          viewDetails={viewDetails}
          reason={reason}
          riderAssigned={riderAssigned}
        />
      )}
      {(!viewDetails || !statusIsDispatch(status)) && (
        <>
          <CardBody
            status={status}
            orders={orders}
            numberOfBrandAndMeal={numberOfBrandAndMeal}
            content={content}
            setHeight={setHeight}
            isGroupOrder={isGroupOrder}
            currentFilter={currentFilter}
            // new props
            cutleryChecked={cutleryChecked}
            isCutlery={isCutlery}
            calculatedOrder={calculatedOrder}
            order={order}
            onCheckCutlery={() => setCutleryChecked(!cutleryChecked)}
            onToggleAccordion={toggleAccordion}
            setActive={setActive}
            setRotate={setRotate}
            isFinished={finishOrder}
            onFinishOrder={() => setFinishOrder(!finishOrder)}
            subOrders={subOrders}
            selectedMeals={selectedMeals}
            selectedAddons={selectedAddons}
            onAddonSelect={handleAddonSelect}
            onBulkAddonSelect={handleBulkAddonSelect}
            onMealSelect={handleMealSelect}
            setViewDetails={setViewDetails}
            printStickerMeals={printStickerMeals}
            setPrintStickerMeals={setPrintStickerMeals}
          />

          <OrderReceipt ref={receiptRef} {...getReceiptData()} />

          <CardFooter
            status={status}
            order={order}
            time={time}
            role={role}
            riderAssigned={riderAssigned}
            reason={reason}
            receiptRef={receiptRef}
            printStickerMeals={printStickerMeals}
          />
          {showInfoModal && (
            <UserInformationModal
              show={showInfoModal}
              onClose={() => setShowInfoModal(false)}
              order={order}
              isPreOrderPage={isPreOrderPage}
            />
          )}
        </>
      )}
    </CardLayout>
  );
};

export default OrderCard;
